import {
  DetailList,
  formatDateTime,
  formatMoney,
  formatPhone,
  PageEmpty,
  PageError,
  PageHeader,
  PageLoading,
} from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { HoldToConfirm } from "@majitap/ui/arc/hold-to-confirm/hold-to-confirm";
import { Input } from "@majitap/ui/arc/input/input";
import { Select } from "@majitap/ui/arc/select/select";
import { SortableDataTable } from "@majitap/ui/arc/sortable-data-table/sortable-data-table";
import { Textarea } from "@majitap/ui/arc/textarea/textarea";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { Undo2 } from "lucide-react";
import { type ReactNode, useState } from "react";
import { errorOf } from "../../api";
import { useCompanyFormat } from "../../company";
import { useRecordCrumb } from "../../crumb";
import { isAmount } from "../../customers";
import { messageFor } from "../../error-copy";
import {
  canRefund,
  METHOD,
  PAYMENT_STATE,
  type PaymentRecord,
  paymentActions,
  paymentQuery,
  purposeOf,
} from "../../payments";
import { RouterLink } from "../../router-link";
import { meQuery } from "../../session";
import { ActivityPanel } from "../shared/panels";
import { QuickbooksStatus } from "../shared/sync-status";
import styles from "./payments.module.css";

/** How long Refund must be held: money leaves. */
export const REFUND_HOLD_MS = 1500;

/** One payment (P8-15): who paid, for what, its refunds, and a refund when it can still have one. */
export function PaymentPage(): ReactNode {
  const { paymentId } = useParams({ from: "/console/payments/$paymentId" });
  const { data: me } = useSuspenseQuery(meQuery);
  const permissions = me?.permissions ?? [];
  const payment = useQuery(paymentQuery(paymentId));
  useRecordCrumb(payment.data?.reference);
  if (payment.status === "pending") return <PageLoading label="Loading the payment" />;
  if (payment.status === "error") {
    const error = errorOf(payment.error);
    if (error.code === "PAYMENT_NOT_FOUND")
      return <PageEmpty title="No such payment" description="It may be in another company, or the link is wrong." />;
    return <PageError message={messageFor(error)} onRetry={() => payment.refetch()} />;
  }
  return <PaymentView payment={payment.data} permissions={permissions} />;
}

function PaymentView({ payment: p, permissions }: { payment: PaymentRecord; permissions: string[] }) {
  const format = useCompanyFormat();
  const money = (v: string) => formatMoney(v, { ...format, currency: p.currency });
  const [label, tone] = PAYMENT_STATE[p.status];
  const payerHref = p.payer ? `/${p.payer.kind === "business" ? "businesses" : "customers"}/${p.payer.id}` : null;
  return (
    <>
      <PageHeader
        title={p.reference}
        description={[
          p.direction === "outbound" ? `${money(p.amount)} out` : money(p.amount),
          METHOD[p.method],
          purposeOf(p),
          p.payer?.name,
        ]
          .filter(Boolean)
          .join(" · ")}
      />
      {p.status === "failed" && p.failure_reason ? (
        <Alert tone="danger" title="This payment failed">
          {p.failure_reason}
        </Alert>
      ) : null}
      <div className={styles.columns}>
        <div className={styles.main}>
          <section className={styles.card}>
            <DetailList
              items={[
                { label: "Status", value: <Badge tone={tone}>{label}</Badge> },
                { label: "Amount", value: money(p.amount) },
                { label: "Method", value: METHOD[p.method] },
                { label: "Receipt number", value: p.receipt_number },
                {
                  label: "Paid by",
                  value:
                    p.payer && payerHref ? (
                      <RouterLink href={payerHref} className={styles.link}>
                        {p.payer.name}
                      </RouterLink>
                    ) : null,
                },
                { label: "From phone", value: formatPhone(p.payer_phone) || null },
                {
                  label: "For",
                  value: p.order_id ? (
                    <Link to="/orders/$orderId" params={{ orderId: p.order_id }} className={styles.link}>
                      Its order
                    </Link>
                  ) : p.invoice_id ? (
                    <Link to="/invoices/$invoiceId" params={{ invoiceId: p.invoice_id }} className={styles.link}>
                      Its invoice
                    </Link>
                  ) : p.parent_payment_id ? (
                    <Link to="/payments/$paymentId" params={{ paymentId: p.parent_payment_id }} className={styles.link}>
                      The payment refunded
                    </Link>
                  ) : (
                    purposeOf(p)
                  ),
                },
                {
                  label: "Wallet",
                  value: p.wallet_id ? (
                    <Link to="/wallets/$walletId" params={{ walletId: p.wallet_id }} className={styles.link}>
                      The payer's wallet
                    </Link>
                  ) : null,
                },
                { label: "Succeeded", value: p.succeeded_at ? formatDateTime(p.succeeded_at, format) : null },
                { label: "Started", value: formatDateTime(p.created_at, format) },
              ]}
            />
          </section>
          {p.refunds.length ? (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Refunds</h2>
              <SortableDataTable
                caption="Refunds"
                rowKey="id"
                rows={p.refunds}
                itemName={{ one: "refund", other: "refunds" }}
                columns={[
                  {
                    key: "reference",
                    label: "Refund",
                    sortable: false,
                    render: (_v, r) => (
                      <Link to="/payments/$paymentId" params={{ paymentId: r.id }} className={styles.link}>
                        {r.reference}
                      </Link>
                    ),
                  },
                  {
                    key: "method",
                    label: "To",
                    sortable: false,
                    render: (_v, r) => (r.wallet_id ? "Wallet" : "Outside the system"),
                  },
                  {
                    key: "amount",
                    label: "Amount",
                    numeric: true,
                    sortable: false,
                    render: (_v, r) => money(r.amount),
                  },
                  {
                    key: "status",
                    label: "Status",
                    sortable: false,
                    render: (_v, r) => <Badge tone={PAYMENT_STATE[r.status][1]}>{PAYMENT_STATE[r.status][0]}</Badge>,
                  },
                  {
                    key: "created_at",
                    label: "When",
                    sortable: false,
                    render: (_v, r) => formatDateTime(r.created_at, format),
                  },
                ]}
              />
            </section>
          ) : null}
          {permissions.includes("staff.manage") ? (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Activity</h2>
              <ActivityPanel targetType="payment" id={p.id} label="What happened to this payment" />
            </section>
          ) : null}
        </div>
        <aside className={styles.side}>
          {canRefund(p, permissions) && p.refundable ? (
            <RefundPanel paymentId={p.id} refundable={p.refundable} currency={p.currency} />
          ) : null}
          {p.status === "succeeded" ? <QuickbooksStatus entityType="payment" entityId={p.id} /> : null}
        </aside>
      </div>
    </>
  );
}

/**
 * Refund (`payments.refund`): up to what the API says is still refundable, a reason, where the
 * money goes, then a press and hold. The API refuses more (`REFUND_EXCEEDS_PAYMENT`).
 */
export function RefundPanel({
  paymentId,
  refundable,
  currency,
}: {
  paymentId: string;
  refundable: string;
  currency: string;
}) {
  const format = useCompanyFormat();
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState(refundable);
  const [reason, setReason] = useState("");
  const [to, setTo] = useState<"wallet" | "manual">("wallet");
  const [confirmed, setConfirmed] = useState<boolean | undefined>(undefined);
  const ready = isAmount(amount) && !/^0+(\.0+)?$/.test(amount.trim()) && reason.trim().length > 0;
  const refund = useMutation({
    mutationFn: () => paymentActions.refund(paymentId, { amount: amount.trim(), reason: reason.trim(), to }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["payment", paymentId] });
      await queryClient.invalidateQueries({ queryKey: ["payments"] });
    },
    onError: () => setConfirmed(false),
  });
  return (
    <section className={styles.card} aria-label="Refund">
      <h2 className={styles.sectionTitle}>Refund</h2>
      <p className={styles.muted}>Up to {formatMoney(refundable, { ...format, currency })} can still be refunded.</p>
      <Input label="Amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
      <Textarea label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
      <Select
        label="Refund to"
        value={to}
        onValueChange={(v) => setTo(v as "wallet" | "manual")}
        description={
          to === "wallet"
            ? "Credited to their wallet at once, for their next order."
            : "Already sent back outside the system (an M-Pesa reversal, cash): this records it."
        }
        options={[
          { value: "wallet", label: "Their wallet" },
          { value: "manual", label: "Sent back another way" },
        ]}
      />
      <HoldToConfirm
        label="Hold to refund"
        confirmedLabel="Refunded"
        icon={<Undo2 size={16} strokeWidth={1.75} />}
        duration={REFUND_HOLD_MS}
        disabled={!ready || refund.isPending}
        confirmed={confirmed}
        onConfirm={() => {
          setConfirmed(undefined);
          refund.mutate();
        }}
      />
      {refund.error ? (
        <Alert tone="danger" title="Not refunded">
          {messageFor(errorOf(refund.error))}
        </Alert>
      ) : null}
    </section>
  );
}
